// ClaudeCode: Create entry form — encrypts client-side before sending to server
import React, { useState } from 'react';
import { createEntry } from '../api';
import { encrypt } from '../crypto';

interface CreateEntryProps {
  cryptoKey: CryptoKey;
  onCreated: () => void;
  onCancel: () => void;
}

export function CreateEntry({ cryptoKey, onCreated, onCancel }: CreateEntryProps) {
  const [title, setTitle] = useState('');
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [tier, setTier] = useState(4);
  const [category, setCategory] = useState('');
  const [url, setUrl] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !value.trim()) {
      setError('Title and value are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Encrypt value client-side
      const encryptedValue = await encrypt(value, cryptoKey);
      const encryptedNotes = notes.trim()
        ? await encrypt(notes, cryptoKey)
        : undefined;

      const tags = tagInput
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      await createEntry({
        title: title.trim(),
        encryptedValue,
        tier,
        category: category.trim() || undefined,
        encryptedNotes,
        url: url.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });

      onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 600 }}>
      <button onClick={onCancel} style={backBtnStyle}>Cancel</button>

      <h2
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: '#fafafa',
          margin: '16px 0 24px',
        }}
      >
        New Entry
      </h2>

      <form onSubmit={handleSubmit}>
        {/* Title */}
        <label style={labelStyle}>Title *</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Bank of America login"
          autoFocus
          required
          style={inputStyle}
        />

        {/* Secret value */}
        <label style={labelStyle}>Secret Value *</label>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Password, API key, account number, etc."
          required
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace' }}
        />

        {/* Notes */}
        <label style={labelStyle}>Notes (optional, encrypted)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional context, recovery instructions, etc."
          rows={2}
          style={{ ...inputStyle, resize: 'vertical' }}
        />

        {/* Tier */}
        <label style={labelStyle}>Security Tier</label>
        <select
          value={tier}
          onChange={(e) => setTier(parseInt(e.target.value, 10))}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value={4}>Tier 4 — Trivial (wifi, library cards)</option>
          <option value={3}>Tier 3 — Convenience (insurance, school logins)</option>
          <option value={2}>Tier 2 — Sensitive (bank, SSN, passport)</option>
          <option value={1}>Tier 1 — Irreplaceable (recovery codes, seeds)</option>
        </select>

        {/* Category */}
        <label style={labelStyle}>Category (optional)</label>
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="e.g. banking, crypto, work"
          style={inputStyle}
        />

        {/* URL */}
        <label style={labelStyle}>URL / Domain (optional)</label>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="e.g. bankofamerica.com"
          style={inputStyle}
        />

        {/* Tags */}
        <label style={labelStyle}>Tags (comma-separated)</label>
        <input
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          placeholder="e.g. bank, personal, important"
          style={inputStyle}
        />

        {error && (
          <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '10px 0',
            background: loading ? '#27272a' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 15,
            fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? 'Encrypting...' : 'Save Entry'}
        </button>
      </form>
    </div>
  );
}

const backBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#71717a',
  fontSize: 14,
  cursor: 'pointer',
  padding: 0,
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#a1a1aa',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: '#09090b',
  border: '1px solid #27272a',
  borderRadius: 6,
  color: '#fafafa',
  fontSize: 15,
  outline: 'none',
  marginBottom: 16,
};
